/* <wrapt-dispatch-promise> — the dispatch countdown (A1, A2).

   Upgrades the server-rendered panel in place. Everything is computed in the
   warehouse's timezone, never the buyer's: the promise is about when Wrapt
   posts the box, so a customer browsing from Sydney sees the same London
   answer.

   FOUR STATES. Express only; Standard never takes the Saturday route.

     Mon-Thu before noon      -> "ships today",            clock to today's noon
     Mon-Thu noon onwards     -> "ships tomorrow",         clock to tomorrow's noon
     Fri before noon          -> "ships today",            clock to today's noon
     Fri noon to 23:59        -> "ships Saturday morning", clock to midnight
     Sat and Sun, all day     -> "ships Monday"            NO CLOCK

   The weekend drops the clock deliberately. A timer that resolves to a two-day
   wait works against itself; the day is the useful fact, so it states the day.

   Saturday dispatch runs on bank holiday weekends too — a Friday order before
   midnight ships Saturday morning regardless of what Monday is. The ONLY place
   a bank holiday moves the promise is the named day: where the copy would say
   "ships Monday" and that Monday is closed, it says "ships Tuesday". Both fall
   out of walking the working-day calendar for the name while leaving the
   Saturday route alone.

   Never renders a delivery date. Ships-when, not lands-when. */

(function () {
  if (customElements.get('wrapt-dispatch-promise')) return;

  var DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  var DAY_SECONDS = 86400;

  /* Intl is the only thing that gets British Summer Time right without a tz
     database, and it is what makes the BST/GMT switch weekends a non-event. */
  function nowInZone(timeZone) {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    var out = {};
    parts.forEach(function (part) {
      if (part.type !== 'literal') out[part.type] = parseInt(part.value, 10);
    });
    if (out.hour === 24) out.hour = 0; // some engines render midnight as 24
    return out;
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function toISO(y, m, d) { return y + '-' + pad(m) + '-' + pad(d); }

  /* Calendar arithmetic in UTC so a daylight-saving boundary cannot drag it an
     hour either way. These are dates, not instants. */
  function addDays(y, m, d, days) {
    var date = new Date(Date.UTC(y, m - 1, d));
    date.setUTCDate(date.getUTCDate() + days);
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      weekday: date.getUTCDay(),
    };
  }

  function parseClock(value, fallbackHour, fallbackMinute) {
    var match = /^(\d{1,2}):(\d{2})$/.exec((value || '').trim());
    if (!match) return fallbackHour * 3600 + fallbackMinute * 60;
    var hour = parseInt(match[1], 10);
    var minute = parseInt(match[2], 10);
    if (isNaN(hour) || hour < 0 || hour > 23) hour = fallbackHour;
    if (isNaN(minute) || minute < 0 || minute > 59) minute = fallbackMinute;
    return hour * 3600 + minute * 60;
  }

  /* The inline format keeps the old "3h 12m" phrasing; the ticket shows cells. */
  function formatRemaining(seconds) {
    var minutes = Math.ceil(seconds / 60);
    var hours = Math.floor(minutes / 60);
    var mins = minutes % 60;
    if (hours > 0) return hours + 'h ' + mins + 'm';
    return mins + 'm';
  }

  function fill(template, values) {
    return String(template || '').replace(/\{(time|day)\}/g, function (_, key) {
      return values[key] != null ? values[key] : '';
    });
  }

  /* The same substitution, but returning segments instead of a string.

     THE POINT IS THE CLOCK. Some surfaces -- the BYO hero -- mark the time
     remaining with a highlight, and that needs the value in its own element.
     Returning parts rather than text keeps that a styling concern: there is
     still one render path and one set of copy, and a surface that does not
     highlight simply never styles the span. */
  function fillParts(template, values) {
    var src = String(template || '');
    var parts = [];
    var re = /\{(time|day)\}/g;
    var last = 0;
    var m;
    while ((m = re.exec(src)) !== null) {
      if (m.index > last) parts.push({ text: src.slice(last, m.index) });
      var value = values[m[1]] != null ? String(values[m[1]]) : '';
      if (value) parts.push({ text: value, clock: m[1] === 'time' });
      last = m.index + m[0].length;
    }
    if (last < src.length) parts.push({ text: src.slice(last) });
    return parts;
  }

  class DispatchPromise extends HTMLElement {
    connectedCallback() {
      this.timeZone = this.dataset.timezone || 'Europe/London';
      this.cutoff = parseClock(this.dataset.cutoff, 12, 0);
      this.fridayCutoff = parseClock(this.dataset.fridayCutoff, 23, 59);
      this.isTicket = this.dataset.format === 'ticket';
      this.closedDates = (this.dataset.nonDispatchDates || '')
        .split(',')
        .map(function (value) { return value.trim(); })
        .filter(Boolean);

      this.output = this.querySelector('.wrapt-dispatch__text') || this;
      this.cells = {};
      var self = this;
      this.querySelectorAll('.wrapt-ticket__digits').forEach(function (node) {
        self.cells[node.dataset.unit] = node;
      });
      this.shipNode = this.querySelector('.wrapt-ticket__ship');
      this.leadNode = this.querySelector('.wrapt-ticket__lead');
      this.srNode = this.querySelector('.wrapt-ticket__sr');

      this.render();
      this.startTicking();

      this.onVisibility = this.onVisibility.bind(this);
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.stopTicking();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* A tab left open over the weekend must not still be offering Friday's
       promise when someone comes back to it on Monday. */
    onVisibility() {
      if (!document.hidden) this.render();
    }

    /* Once a second, because the panel shows seconds. The inline format only
       changes once a minute but the cost of a no-op tick is nil. */
    startTicking() {
      this.stopTicking();
      this.timer = window.setInterval(this.render.bind(this), 1000);
    }

    stopTicking() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    isDispatchDay(iso, weekday) {
      if (weekday === 0 || weekday === 6) return false;
      return this.closedDates.indexOf(iso) === -1;
    }

    /* The next day we actually post on, and how far away it is. Two weeks is
       far more than any real closure and stops a bad metafield looping. */
    nextDispatchDay(now) {
      for (var offset = 1; offset <= 14; offset++) {
        var next = addDays(now.year, now.month, now.day, offset);
        if (this.isDispatchDay(toISO(next.year, next.month, next.day), next.weekday)) {
          return { offset: offset, weekday: next.weekday };
        }
      }
      return null;
    }

    state() {
      var now = nowInZone(this.timeZone);
      if (!now.year) return null; // Intl gave us nothing usable — keep the fallback.

      var today = addDays(now.year, now.month, now.day, 0);
      var todayISO = toISO(today.year, today.month, today.day);
      var openToday = this.isDispatchDay(todayISO, today.weekday);
      var secondsNow = now.hour * 3600 + now.minute * 60 + now.second;
      var next = this.nextDispatchDay(now);

      if (openToday && secondsNow < this.cutoff) {
        return { name: 'today', remaining: this.cutoff - secondsNow };
      }

      /* Friday past noon. The clock runs to midnight tonight, not to the
         Saturday dispatch itself — midnight is the moment the offer expires. */
      /* The cutoff names a minute, so the window runs to the end of it —
         23:59 must include 23:59:59, not expire 59 seconds early. */
      if (openToday && today.weekday === 5 && secondsNow <= this.fridayCutoff + 59) {
        return { name: 'saturday', remaining: DAY_SECONDS - secondsNow };
      }

      /* Never on a Saturday or Sunday, even though Monday is literally the next
         dispatch day from Sunday. The weekend states are always the named day
         with no clock — a timer resolving to a two-day wait works against
         itself, and on Sunday "ships tomorrow" would start one anyway. */
      var isWeekend = today.weekday === 0 || today.weekday === 6;
      if (!isWeekend && next && next.offset === 1) {
        return { name: 'tomorrow', remaining: DAY_SECONDS - secondsNow + this.cutoff };
      }

      if (next) return { name: 'day', day: DAY_NAMES[next.weekday] };
      return null; // Nothing sensible to say — the static fallback stands.
    }

    shipPhrase(state) {
      if (state.name === 'today') return this.dataset.shipToday || 'ships today';
      if (state.name === 'tomorrow') return this.dataset.shipTomorrow || 'ships tomorrow';
      if (state.name === 'saturday') return this.dataset.shipSaturday || 'ships Saturday morning';
      return fill(this.dataset.shipDay || 'ships {day}', { day: state.day });
    }

    render() {
      var state = this.state();
      if (!state) return;
      if (this.isTicket) this.renderTicket(state);
      else this.renderInline(state);
    }

    renderInline(state) {
      var parts;
      if (state.name === 'today') {
        parts = fillParts(this.dataset.templateToday, { time: formatRemaining(state.remaining) });
      } else if (state.name === 'tomorrow') {
        parts = fillParts(this.dataset.templateTomorrow, {});
      } else if (state.name === 'saturday') {
        /* Kept as a fallback for a theme that predates the setting. The
           sentence used to be built here, which meant it was the one state
           nobody could edit. */
        parts = this.dataset.templateSaturday
          ? fillParts(this.dataset.templateSaturday, {})
          : [{ text: 'Order now and it ' + this.shipPhrase(state) }];
      } else {
        parts = fillParts(this.dataset.templateDay, { day: state.day });
      }

      /* THE SERVICE IS PART OF THE PROMISE, not a decoration on it. The noon
         cutoff and the Friday-to-Saturday rule are Express's terms; Standard
         is 3 to 5 working days with no cutoff at all, so "ships Tuesday" is
         only true of one of the two services on offer.

         Appended rather than written into each line, for two reasons. It is
         then one fact in one setting and cannot drift between surfaces -- and
         the BYO sections carry their own countdown copy in a template that
         code must not rewrite, so a placeholder token would never have
         reached them. This does. */
      var note = (this.dataset.expressNote || '').trim();
      if (note) parts.push({ text: ' ' + note });

      var message = parts.map(function (part) { return part.text; }).join('');
      if (!message || message === this.lastInline) return;
      this.lastInline = message;

      /* Rebuilt rather than patched. The segments change shape between states
         -- only "today" carries a clock -- so there is no stable node to write
         into. Text nodes and textContent throughout: the copy is a theme
         setting, and it never becomes markup. */
      this.output.textContent = '';
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].clock) {
          var span = document.createElement('span');
          span.className = 'wrapt-dispatch__clock';
          span.textContent = parts[i].text;
          this.output.appendChild(span);
        } else {
          this.output.appendChild(document.createTextNode(parts[i].text));
        }
      }
    }

    renderTicket(state) {
      var noClock = state.name === 'day';
      this.dataset.state = state.name;
      this.classList.toggle('wrapt-ticket--noclock', noClock);

      var ship = this.shipPhrase(state);
      var lead = noClock ? 'Order now and it' : 'and it';

      if (this.leadNode && this.leadNode.textContent !== lead) this.leadNode.textContent = lead;
      if (this.shipNode && this.shipNode.textContent !== ship) this.shipNode.textContent = ship;

      if (this.srNode) {
        var tail = this.dataset.tail || '';
        var sentence = lead.charAt(0).toUpperCase() + lead.slice(1) + ' ' + ship + ' ' + tail + '.';
        if (this.srNode.textContent !== sentence) this.srNode.textContent = sentence;
      }

      if (noClock) return;

      var remaining = Math.max(0, state.remaining);
      var hours = Math.floor(remaining / 3600);
      var minutes = Math.floor((remaining % 3600) / 60);
      var seconds = remaining % 60;
      this.setCell('hours', pad(hours));
      this.setCell('minutes', pad(minutes));
      this.setCell('seconds', pad(seconds));
    }

    setCell(unit, value) {
      var node = this.cells[unit];
      if (node && node.textContent !== value) node.textContent = value;
    }
  }

  customElements.define('wrapt-dispatch-promise', DispatchPromise);
})();
